import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { CEMENT_TYPES } from './pages/OpcTest/cementTypes';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import SessionPermissionWatcher from './components/SessionPermissionWatcher';
import ErrorBoundary from './components/ErrorBoundary';
import { NotificationProvider } from './contexts/NotificationContext';
import { SearchProvider } from './contexts/SearchContext';
import { ToastProvider } from './contexts/ToastContext';

// Layouts - kept as regular imports (needed for initial render)
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';

// Route Guards - kept as regular imports (needed for route protection)
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import CeoRoute from './components/CeoRoute';
import AdminRoute from './components/AdminRoute';
import AuthBrandedLoader from './components/AuthBrandedLoader';
import RouteSkeleton from './components/RouteSkeleton';

const AUTH_PATH_PREFIXES = ['/signin', '/signup', '/verify-otp', '/forgot-password', '/reset-password'];

// Order matters: form/edit suffix takes precedence over the list it belongs to,
// and detail (numeric id) takes precedence over the list root.
const FORM_PATTERNS = [
  /\/create$/,
  /\/edit$/,
  /\/register$/,
];

const DETAIL_PATTERNS = [
  /^\/customers\/\d+$/,
  /^\/enquiries\/\d+$/,
  /^\/quotations\/\d+$/,
  /^\/service-request-forms\/\d+$/,
  /^\/proforma-invoices\/\d+$/,
  /^\/invoices\/\d+$/,
];

const LIST_PATTERNS = [
  /^\/customers\/?$/,
  /^\/enquiries\/?$/,
  /^\/quotations\/?$/,
  /^\/service-request-forms\/?$/,
  /^\/proforma-invoices\/?$/,
  /^\/invoices\/?$/,
  /^\/notifications\/?$/,
];

function pickVariant(pathname) {
  if (AUTH_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return 'auth';
  if (FORM_PATTERNS.some((re) => re.test(pathname))) return 'form';
  if (DETAIL_PATTERNS.some((re) => re.test(pathname))) return 'detail';
  if (LIST_PATTERNS.some((re) => re.test(pathname))) return 'list';
  return 'generic';
}

function RouteAwarePageLoader() {
  const location = useLocation();
  const variant = pickVariant(location.pathname);
  if (variant === 'auth') return <AuthBrandedLoader />;
  return <RouteSkeleton variant={variant} />;
}

// Auth Pages - lazy loaded
const SignUp = lazy(() => import('./pages/auth/SignUp'));
const SignIn = lazy(() => import('./pages/auth/SignIn'));
const VerifyOtp = lazy(() => import('./pages/auth/VerifyOtp'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const AdminConsole = lazy(() => import('./pages/Admin/AdminConsole'));
const AdminTaxSettings = lazy(() => import('./pages/AdminTaxSettings'));
const ChangePassword = lazy(() => import('./pages/Settings/ChangePassword'));
const NotificationPreferences = lazy(() => import('./pages/Settings/NotificationPreferences'));

// Dashboard - loaded eagerly as it's the main entry point
const Dashboard = lazy(() => import('./pages/Dashboard'));

// Heavy pages - lazy loaded for better initial load performance
const MaterialScope = lazy(() => import('./pages/MaterialScope/MaterialScopeV2'));
const CustomerList = lazy(() => import('./pages/CustomerList'));
const CustomerRegistration = lazy(() => import('./pages/CustomerRegistration'));
const CustomerEdit = lazy(() => import('./pages/CustomerEdit'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail'));
const EnquiryList = lazy(() => import('./pages/EnquiryList'));
const CreateEnquiry = lazy(() => import('./pages/CreateEnquiry'));
const EnquiryDetails = lazy(() => import('./pages/EnquiryDetails'));
const QuotationList = lazy(() => import('./pages/QuotationList'));
const CreateQuotation = lazy(() => import('./pages/CreateQuotation'));
const QuotationDetails = lazy(() => import('./pages/QuotationDetails'));
const WorkOrderList = lazy(() => import('./pages/WorkOrderList'));
const CreateWorkOrder = lazy(() => import('./pages/CreateWorkOrder'));
const WorkOrderDetails = lazy(() => import('./pages/WorkOrderDetails'));
const InvoiceList = lazy(() => import('./pages/InvoiceList'));
const CreateInvoice = lazy(() => import('./pages/CreateInvoice'));
const ProformaInvoiceForm = lazy(() => import('./pages/ProformaInvoiceForm'));
const InvoiceDetails = lazy(() => import('./pages/InvoiceDetails'));
const FinalInvoiceList = lazy(() => import('./pages/FinalInvoiceList'));
const CreateFinalInvoice = lazy(() => import('./pages/CreateFinalInvoice'));
const FinalInvoiceDetails = lazy(() => import('./pages/FinalInvoiceDetails'));
const NotificationsPage = lazy(() => import('./pages/Notifications'));
const Utility = lazy(() => import('./pages/Utility'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));
const CubeTestList = lazy(() => import('./pages/CubeTest/CubeTestList'));
const CubeTestGuide = lazy(() => import('./pages/CubeTest/CubeTestGuide'));
const CubeTestWorkspace = lazy(() => import('./pages/CubeTest/CubeTestWorkspace'));
const OpcTestList = lazy(() => import('./pages/OpcTest/OpcTestList'));
const OpcTestGuide = lazy(() => import('./pages/OpcTest/OpcTestGuide'));
const OpcTestWorkspace = lazy(() => import('./pages/OpcTest/OpcTestWorkspace'));
const SteelTestGuide      = lazy(() => import('./pages/SteelTest/SteelTestGuide'));
const SteelTestList       = lazy(() => import('./pages/SteelTest/SteelTestList'));
const SteelTestWorkspace  = lazy(() => import('./pages/SteelTest/SteelTestWorkspace'));
const JobCardList = lazy(() => import('./pages/JobCard/JobCardList'));
const JobCardDetail = lazy(() => import('./pages/JobCard/JobCardDetail'));
const MyAssignments = lazy(() => import('./pages/JobCard/MyAssignments'));

function LegacyWorkOrderRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/workorders/, '') || '/';
  const to = rest === '/' ? '/service-request-forms' : `/service-request-forms${rest}`;
  return <Navigate to={to} replace />;
}

/** /test-execution/opc/:id → /test-execution/cement/:id */
function LegacyOpcWorkspaceRedirect() {
  const { id } = useParams();
  return <Navigate to={`/test-execution/cement/${id}`} replace />;
}


function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <RealtimeProvider>
              <SessionPermissionWatcher />
              <NotificationProvider>
                <SearchProvider>
              <ErrorBoundary>
              <Suspense fallback={<RouteAwarePageLoader />}>
            <Routes>
              {/* Public Routes (Auth) */}
              <Route element={<PublicRoute><AuthLayout /></PublicRoute>}>
                <Route path="/signup" element={<SignUp />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/verify-otp" element={<VerifyOtp />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
              </Route>

              {/* Protected Routes (Dashboard) */}
              <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/customers" element={<CustomerList />} />
                <Route path="/customers/register" element={<CustomerRegistration />} />
                <Route path="/customers/:id/edit" element={<CustomerEdit />} />
                <Route path="/customers/:id" element={<CustomerDetail />} />
                <Route path="/enquiries" element={<EnquiryList />} />
                <Route path="/enquiries/create" element={<CreateEnquiry />} />
                <Route path="/enquiries/:id" element={<EnquiryDetails />} />
                <Route path="/enquiries/:id/edit" element={<CreateEnquiry />} />
                <Route path="/quotations" element={<QuotationList />} />
                <Route path="/quotations/create" element={<CreateQuotation />} />
                <Route path="/quotations/:id" element={<QuotationDetails />} />
                <Route path="/quotations/:id/edit" element={<CreateQuotation />} />
                <Route path="/service-request-forms" element={<WorkOrderList />} />
                <Route path="/service-request-forms/create" element={<CreateWorkOrder />} />
                <Route path="/service-request-forms/:id" element={<WorkOrderDetails />} />
                <Route path="/service-request-forms/:id/edit" element={<CreateWorkOrder />} />
                <Route path="/workorders/*" element={<LegacyWorkOrderRedirect />} />
                <Route path="/proforma-invoices" element={<InvoiceList />} />
                {/* v2 SRF-only PI form — accepts ?srfId=N */}
                <Route path="/proforma-invoices/new" element={<ProformaInvoiceForm />} />
                <Route path="/proforma-invoices/create" element={<CreateInvoice />} />
                <Route path="/proforma-invoices/:id" element={<InvoiceDetails />} />
                <Route path="/proforma-invoices/:id/edit" element={<CreateInvoice />} />
                <Route path="/invoices" element={<FinalInvoiceList />} />
                <Route path="/invoices/create" element={<CreateFinalInvoice />} />
                <Route path="/invoices/:id" element={<FinalInvoiceDetails />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/materials" element={<MaterialScope />} />
                <Route path="/utility" element={<Utility />} />
                <Route path="/admin/tax-settings" element={<AdminRoute><AdminTaxSettings /></AdminRoute>} />
                <Route path="/admin" element={<CeoRoute><AdminConsole /></CeoRoute>} />
                <Route path="/admin/signup-settings" element={<CeoRoute><AdminConsole /></CeoRoute>} />
                <Route path="/admin/pending-approvals" element={<CeoRoute><AdminConsole /></CeoRoute>} />
                <Route path="/admin/roles" element={<CeoRoute><AdminConsole /></CeoRoute>} />
                <Route path="/admin/user-roles" element={<CeoRoute><AdminConsole /></CeoRoute>} />
                <Route path="/admin/presence" element={<CeoRoute><AdminConsole /></CeoRoute>} />
                <Route path="/admin/modules" element={<CeoRoute><AdminConsole /></CeoRoute>} />
                <Route path="/test-execution/cube" element={<CubeTestList />} />
                <Route path="/test-execution/cube/guide" element={<CubeTestGuide />} />
                <Route path="/test-execution/cube/:id" element={<CubeTestWorkspace />} />
                {/* Cement type list routes — one per type, resolved before :id */}
                {CEMENT_TYPES.map((ct) => (
                  <Route
                    key={ct.slug}
                    path={`/test-execution/cement/${ct.slug}`}
                    element={<OpcTestList cementType={ct.cementType} title={ct.displayName} />}
                  />
                ))}
                {/* Shared cement guide and workspace */}
                <Route path="/test-execution/cement/guide" element={<OpcTestGuide />} />
                <Route path="/test-execution/cement/:id" element={<OpcTestWorkspace />} />
                {/* Back-compat redirects for old /test-execution/opc paths */}
                <Route path="/test-execution/opc" element={<Navigate to="/test-execution/cement/opc" replace />} />
                <Route path="/test-execution/opc/guide" element={<Navigate to="/test-execution/cement/guide" replace />} />
                <Route path="/test-execution/opc/:id" element={<LegacyOpcWorkspaceRedirect />} />
                {/* HSDB steel bar — guide resolves first (before :id) */}
                <Route path="/test-execution/steel/guide" element={<SteelTestGuide />} />
                <Route path="/test-execution/steel" element={<SteelTestList />} />
                <Route path="/test-execution/steel/:id" element={<SteelTestWorkspace />} />
                <Route path="/job-cards" element={<JobCardList />} />
                <Route path="/job-cards/:id" element={<JobCardDetail />} />
                <Route path="/my-assignments" element={<MyAssignments />} />
                <Route path="/settings/change-password" element={<ChangePassword />} />
                <Route path="/settings/notifications" element={<NotificationPreferences />} />
              </Route>

              {/* Redirects */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
              </ErrorBoundary>
          </SearchProvider>
              </NotificationProvider>
            </RealtimeProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  </BrowserRouter>
  );
}

export default App;
